import React, { useEffect } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { LicenseInfo } from '@mui/x-license'

import getChildRoute from './routes'

// importing App.scss before components & pages so that they can override it later
import './App.scss'

// components
import WithErrorBoundaryWrapper from 'components/WithErrorBoundaryWrapper/WithErrorBoundaryWrapper'
import Loader from './components/Loader'
import Header from './components/Header'
import LeftNav from './components/LeftNav'
import Walkthrough from './components/Walkthrough'
import Footer from './components/Footer'

// actions
import { login, logout, logoutSuccess } from './actions/auth-actions'

// selectors
import { getAuth, isAuthValid } from './selectors/session-selectors'

import AuthService from './services/auth-service'
import { REACT_APP_MUI_LICENSE_KEY } from './constants/constants'

const APP_VERSION = process.env.REACT_APP_VERSION

LicenseInfo.setLicenseKey(REACT_APP_MUI_LICENSE_KEY)

const App = ({
  auth,
  login,
  logoutSuccess,
  isAuthValid,
  pathname,
  user,
  logout,
  loading,
  beacons,
  isWalkthroughEnabled
}) => {
  // Keyed on the token, not the auth object: after SSO the same session can arrive as different objects
  // (SsoCallbackPage remounts), and re-running on identity alone loops login -> re-render -> login
  const token = auth && auth.token
  useEffect(() => {
    if (token && isAuthValid && pathname !== '/sso-callback') {
      login({ auth })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, isAuthValid, pathname, login])

  useEffect(() => {
    // Clear session state and redirect if logged out from another tab. Event triggered in auth-service
    window.addEventListener('storage', event => {
      if (event.key === 'logout-event') {
        console.info('A logout event has been detected')
        logoutSuccess()
        AuthService.redirectToLogin(history.replace, window.location)
      }
    })
  }, [logoutSuccess])

  return (
    <div className="app">
      {<Header user={isAuthValid ? user : null} onLogout={logout} pathname={pathname} />}
      {user && isAuthValid && user.isOnboarded && <LeftNav />}
      <div className="page-content">
        {(() => {
          if (!user && loading) {
            return (
              <div className="page-container">
                <Loader />
              </div>
            )
          } else {
            return getChildRoute()
          }
        })()}
        <Footer appVersion={APP_VERSION} isAuthValid={isAuthValid} />
      </div>
      {user && isWalkthroughEnabled && user.isOnboarded && <Walkthrough beacons={beacons} pathname={pathname} />}
    </div>
  )
}

App.propTypes = {
  onMount: PropTypes.func,
  login: PropTypes.func.isRequired,
  logout: PropTypes.func.isRequired,
  logoutSuccess: PropTypes.func.isRequired,
  user: PropTypes.object,
  sessionState: PropTypes.string,
  auth: PropTypes.object,
  isAuthValid: PropTypes.bool.isRequired,
  children: PropTypes.element,
  loading: PropTypes.bool,
  history: PropTypes.object.isRequired,
  beacons: PropTypes.object,
  pathname: PropTypes.string,
  isWalkthroughEnabled: PropTypes.bool
}

// Connect
const mapStateToProps = state => ({
  loading: state.session.loading,
  user: state.session.user,
  auth: getAuth(state),
  isAuthValid: isAuthValid(state),
  isWalkthroughEnabled: state.walkthrough.isEnabled,
  pathname: state.router.location.pathname || ''
})

const mapDispatchToProps = dispatch => ({
  login: auth => dispatch(login(auth)),
  logout: () => dispatch(logout()),
  logoutSuccess: () => dispatch(logoutSuccess())
})

export default WithErrorBoundaryWrapper(connect(mapStateToProps, mapDispatchToProps)(App))
